import { json, redirect } from "@remix-run/cloudflare";
import type { LoaderFunction, TypedResponse } from "@remix-run/cloudflare";
import { useLoaderData } from "@remix-run/react";
import Carousel from "~/components/carousel.component";
import ItemsList from "~/components/cart/items-list.component";
import OrderSummary from "~/components/cart/order-summary.component";
import { admirer_cart } from "~/cookies";
import surpriseFaceHref from "~/images/icon-faces/surprise.svg";
import CartGQL from "~/shopify/API/cart.server";
import { getProduct } from "~/shopify/API/product.server";

import { NeumButton } from "~/components/neum.component";
import type { CartFlatten, CartResponse } from "~/types";

import { useState } from "react";
import { MIN_QUANTITY_AVAILABLE } from "~/constants";
import useScript from "~/hooks/useScript";
import { getSession } from "~/sessions";
import { getCollectionProducts } from "~/shopify/API/collection-products.server";
import CustomerGQL from "~/shopify/API/customer.server";
import { getRecentProducts } from "~/shopify/API/recent-products.server";
import { CountryCode } from "~/shopify/types/gql/graphql";
import type {
  CartBuyerIdentityInput,
  Customer,
  CustomerAccessToken,
  DeliveryAddressInput,
  Scalars,
} from "~/shopify/types/gql/graphql";
import { flattenConnection } from "~/shopify/utilities/flattenConnection";

export async function action({ request }: { request: Request }) {
  const formData = await request.formData();
  const checkoutUrl = formData.get("checkoutUrl") as string;
  return redirect(checkoutUrl);
}

const transformCustomerAddressToDeliveryAddress = (customer: Customer): DeliveryAddressInput[] => {
  const customerAddresses = flattenConnection(customer.addresses);
  return customerAddresses.map((address) => {
    return {
      deliveryAddress: {
        address1: address.address1,
        address2: address.address2,
        city: address.city,
        company: address.company,
        country: address.country,
        firstName: address.firstName,
        lastName: address.lastName,
        phone: address.phone,
        province: address.province,
        zip: address.zip,
      },
    };
  });
};

const createBuyerIdentity = (customer: Customer, customerAccessToken: string): CartBuyerIdentityInput => {
  return {
    email: customer.email,
    phone: customer.phone,
    // TODO: enable more countries? get the country where the buyer is located.
    countryCode: CountryCode.Us,
    customerAccessToken,
    deliveryAddressPreferences: transformCustomerAddressToDeliveryAddress(customer),
  };
};

const cartLinesNotAvailableForSale = (cartLines: CartFlatten["lines"]): Scalars["ID"][] => {
  return cartLines
    .filter((cartLine) => cartLine.quantity <= 0 || cartLine.merchandise.availableForSale === false)
    .map((cartLine) => cartLine.id);
};

// TODO: fix all this if else logic, it is not readable
export const loader: LoaderFunction = async ({
  request,
}: {
  request: Request;
}): Promise<TypedResponse<CartResponse>> => {
  let category = "";
  const session = await getSession(request.headers.get("Cookie"));
  const customerAccessToken = session.get("customerAccessToken") as CustomerAccessToken;
  const cookieHeader = request.headers.get("Cookie");
  let cartId = ((await admirer_cart.parse(cookieHeader)) as string) ?? "";
  if (cartId) {
    let { cart, error } = await CartGQL.getCart(cartId);

    if (cart) {
      // Associate customer with cart if not already associated
      if (!cart.buyerIdentity.email && customerAccessToken?.accessToken) {
        const { customer } = await CustomerGQL.getCustomer(customerAccessToken.accessToken);
        if (customer) {
          const buyerIdentityInput = createBuyerIdentity(customer, customerAccessToken.accessToken);
          const updatedCart = await CartGQL.cartBuyerIdentityUpdate(cartId, buyerIdentityInput);
          if (updatedCart.cart) {
            cart = updatedCart.cart;
          } else {
            error = updatedCart.error;
          }
        }
      }

      // Remove cart lines/products not available for sale anymore
      const cartLinesNotAvailable = cartLinesNotAvailableForSale(cart.lines);
      if (cartLinesNotAvailable.length > 0) {
        const updatedCart = await CartGQL.cartLinesRemove(cartId, cartLinesNotAvailable);
        if (updatedCart.cart) {
          cart = updatedCart.cart;
          error = updatedCart.error;
        } else {
          error = updatedCart.error;
        }
      }

      // TODO: do not make getProduct call here, lazy load related products on carousel
      const { product } = await getProduct(cart.lines[0].merchandise.product.handle);
      category = product?.collections[0]?.handle ?? "";
      return json({ cart, category, error: error?.message });
    }
    // NOTE: if cart is not found,(maybe deleted after successfull checkout)
    //  we delete cart cookie from session and return empty cart
    if (
      error?.message.includes("expected cart in response") ||
      error?.message.includes("The specified cart does not exist")
    ) {
      cartId = "";
      return json(
        { category },
        {
          headers: {
            "Set-Cookie": await admirer_cart.serialize(cartId, {
              maxAge: 0,
              expires: new Date(0),
            }),
          },
        },
      );
    }
    return json({ error: error?.message, category });
  }
  return json({ category });
};

export default function CartPage() {
  // TODO: do not use as CartResponse, it would hide real issues, we need to fix logic and use typeof loader
  const { cart, category, error } = useLoaderData() as CartResponse;
  const cartProducts = cart
    ? cart.lines.map((line) => ({
        item_id: line.merchandise.id,
        item_name: line.merchandise.product.title,
        currency: line.merchandise.priceV2.currencyCode,
        item_variant: line.merchandise.title,
        price: Number.parseFloat(line.merchandise.priceV2.amount as string),
        quantity: line.quantity,
      }))
    : [];

  useScript(
    "gtag_begin_checkout",
    `
      window.dataLayer = window.dataLayer || [];
      dataLayer.push({
        event: "begin_checkout",
        items: ${JSON.stringify(cartProducts)},
      });
      `,
  );

  // TODO show error in alert if exists, it could be cleaning cart error or other errors
  if (error) {
    console.log("error in loader or api", error);
  }

  const [cartState, setCartState] = useState(cart);
  return (
    <div className="px-4">
      <main>
        <h1 className="text-shadow-2xl pl-1 pt-4 text-xl font-bold leading-5 sm:pt-8 sm:text-3xl">Shopping Cart</h1>
        {cartState ? (
          <div>
            <div className="mt-2 lg:grid lg:grid-cols-12 lg:items-start ">
              <ItemsList cart={cartState} setCartState={setCartState} />
              <OrderSummary cart={cartState} />
            </div>
            <Carousel category={category} title="You may also like" />
          </div>
        ) : (
          <div>
            <img className="m-auto my-4 w-1/4 content-center md:my-8 md:w-32" src={surpriseFaceHref} alt="Empty cart" />
            <p className="mb-3 text-center text-lg font-bold md:text-3xl">Oh, oh!</p>
            <p className="text-center text-lg font-normal md:text-3xl">Your Admirer Cart is empty.</p>
            <p className="p-3 text-center text-sm font-normal text-admirerBlack-700 dark:text-admirerBlack-100 md:p-8 md:text-2xl">
              Your Shopping Cart lives to serve. Give it purpose — fill it with clothing, household supplies,
              electronics, and more. Continue shopping on the Admirer.store homepage.
            </p>
            <div className="m-auto w-5/6 content-center md:w-3/12">
              <a className="text-center font-bold " href="/">
                <NeumButton rounded={true}>Shop by category</NeumButton>
              </a>
            </div>
            <Carousel category={category} title="Our most recent products" />
          </div>
        )}
      </main>
    </div>
  );
}
